import { FacilityType } from '../interfaces/facility';
import { SeverityGrade } from './common';
/**
 * Damage / inspection vocabulary differs per facility type. The platform supports two facility
 * types — bridge and tunnel — each with its own set of component types and damage types, valid
 * severity grades, and per-damage-type measurement schemas.
 *
 * The frontend works in ids; the `code` carried alongside each id is set automatically on selection
 * (transparent to the user). Labels are not stored here — they live in the i18n locale files,
 * namespaced by facility type (e.g. `object.componentType.bridge.*`, `object.defectType.tunnel.*`).
 */
export var DamageCategory;
(function (DamageCategory) {
    DamageCategory["Linear"] = "linear";
    DamageCategory["Surface"] = "surface";
    DamageCategory["Point"] = "point";
    DamageCategory["Volumetric"] = "volumetric";
    DamageCategory["Environmental"] = "environmental";
    DamageCategory["StructuralDamage"] = "structural_damage";
    DamageCategory["Other"] = "other";
})(DamageCategory || (DamageCategory = {}));
// ===== Component types =====
export var TunnelComponentType;
(function (TunnelComponentType) {
    TunnelComponentType["Crown"] = "crown";
    TunnelComponentType["Sidewall"] = "sidewall";
    TunnelComponentType["Invert"] = "invert";
    TunnelComponentType["Portal"] = "portal";
    TunnelComponentType["Ceiling"] = "ceiling";
    TunnelComponentType["Attachments"] = "attachments";
})(TunnelComponentType || (TunnelComponentType = {}));
export var BridgeComponentType;
(function (BridgeComponentType) {
    BridgeComponentType["MainGirder"] = "main_girder";
    BridgeComponentType["CrossBeam"] = "cross_beam";
    BridgeComponentType["Stringer"] = "stringer";
    BridgeComponentType["DeckSlab"] = "deck_slab";
    BridgeComponentType["SuperstructureOther"] = "superstructure_other";
    BridgeComponentType["Pier"] = "pier";
    BridgeComponentType["Abutment"] = "abutment";
    BridgeComponentType["Foundation"] = "foundation";
    BridgeComponentType["SubstructureOther"] = "substructure_other";
    BridgeComponentType["Bearing"] = "bearing";
    BridgeComponentType["ConnectionOther"] = "connection_other";
    BridgeComponentType["ExpansionJoint"] = "expansion_joint";
    BridgeComponentType["FailSafe"] = "fail_safe";
    BridgeComponentType["Curb"] = "curb";
    BridgeComponentType["Guardrail"] = "guardrail";
    BridgeComponentType["Drainage"] = "drainage";
    BridgeComponentType["Pavement"] = "pavement";
    BridgeComponentType["Staircase"] = "staircase";
    BridgeComponentType["SignBoard"] = "sign_board";
    BridgeComponentType["Lighting"] = "lighting";
    BridgeComponentType["InspectionGallery"] = "inspection_gallery";
})(BridgeComponentType || (BridgeComponentType = {}));
// ===== Damage types =====
export var TunnelDamageType;
(function (TunnelDamageType) {
    TunnelDamageType["Crack"] = "crack";
    TunnelDamageType["Delamination"] = "delamination";
    TunnelDamageType["Spalling"] = "spalling";
    TunnelDamageType["Falloff"] = "falloff";
    TunnelDamageType["Deformation"] = "deformation";
    TunnelDamageType["WaterLeakage"] = "water_leakage";
    TunnelDamageType["MaterialDeterioration"] = "material_deterioration";
    TunnelDamageType["RebarExposure"] = "rebar_exposure";
    TunnelDamageType["CavityVoid"] = "cavity_void";
    TunnelDamageType["Efflorescence"] = "efflorescence";
    TunnelDamageType["FreeLime"] = "free_lime";
    TunnelDamageType["RustStaining"] = "rust_staining";
    TunnelDamageType["Honeycombing"] = "honeycombing";
})(TunnelDamageType || (TunnelDamageType = {}));
export var BridgeDamageType;
(function (BridgeDamageType) {
    BridgeDamageType["CorrosionSteel"] = "corrosion_steel";
    BridgeDamageType["CrackSteel"] = "crack_steel";
    BridgeDamageType["BoltLooseningFalloff"] = "bolt_loosening_falloff";
    BridgeDamageType["RuptureSteel"] = "rupture_steel";
    BridgeDamageType["AnticorrosionDeterioration"] = "anticorrosion_deterioration";
    BridgeDamageType["CrackConcrete"] = "crack_concrete";
    BridgeDamageType["SpallingRebarExposure"] = "spalling_rebar_exposure";
    BridgeDamageType["LeakageEfflorescence"] = "leakage_efflorescence";
    BridgeDamageType["PunchingFailure"] = "punching_failure";
    BridgeDamageType["ReinforcementDamage"] = "reinforcement_damage";
    BridgeDamageType["CrackDeckSlab"] = "crack_deck_slab";
    BridgeDamageType["Delamination"] = "delamination";
    BridgeDamageType["JointAbnormality"] = "joint_abnormality";
    BridgeDamageType["RoadSurfaceUnevenness"] = "road_surface_unevenness";
    BridgeDamageType["PavementAnomaly"] = "pavement_anomaly";
    BridgeDamageType["BearingDysfunction"] = "bearing_dysfunction";
    BridgeDamageType["Other"] = "other";
    BridgeDamageType["AnchorageAbnormality"] = "anchorage_abnormality";
    BridgeDamageType["MaterialDeterioration"] = "material_deterioration";
    BridgeDamageType["WaterLeakagePuddling"] = "water_leakage_puddling";
    BridgeDamageType["AbnormalSoundVibration"] = "abnormal_sound_vibration";
    BridgeDamageType["AbnormalDeflection"] = "abnormal_deflection";
    BridgeDamageType["DeformationDefect"] = "deformation_defect";
    BridgeDamageType["SedimentClogging"] = "sediment_clogging";
    BridgeDamageType["SettlementTilt"] = "settlement_tilt";
    BridgeDamageType["Scour"] = "scour";
    BridgeDamageType["None"] = "none";
})(BridgeDamageType || (BridgeDamageType = {}));
/** All severity grades — used when a damage type imposes no grade constraint (i.e. tunnel). */
export const ALL_SEVERITY_GRADES = [
    SeverityGrade.A,
    SeverityGrade.B,
    SeverityGrade.C,
    SeverityGrade.D,
    SeverityGrade.E,
];
// ===== Tunnel spec =====
const TUNNEL_COMPONENT_TYPES = [
    { id: TunnelComponentType.Crown, code: '01' },
    { id: TunnelComponentType.Sidewall, code: '02' },
    { id: TunnelComponentType.Invert, code: '03' },
    { id: TunnelComponentType.Portal, code: '04' },
    { id: TunnelComponentType.Ceiling, code: '05' },
    { id: TunnelComponentType.Attachments, code: '06' },
];
const TUNNEL_DAMAGE_TYPES = [
    {
        id: TunnelDamageType.Crack,
        code: '01',
        category: DamageCategory.Linear,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'length', type: 'number', unit: 'mm', required: true },
            { field: 'width', type: 'number', unit: 'mm', required: true },
            { field: 'pattern', type: 'string', unit: null, required: false },
        ],
    },
    {
        id: TunnelDamageType.Delamination,
        code: '02',
        category: DamageCategory.Surface,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'area', type: 'number', unit: 'm2', required: true },
            { field: 'depth', type: 'number', unit: 'mm', required: false },
        ],
    },
    {
        id: TunnelDamageType.Spalling,
        code: '03',
        category: DamageCategory.Surface,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'area', type: 'number', unit: 'm2', required: true },
            { field: 'depth', type: 'number', unit: 'mm', required: false },
        ],
    },
    {
        id: TunnelDamageType.Falloff,
        code: '04',
        category: DamageCategory.Surface,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'area', type: 'number', unit: 'm2', required: true },
            { field: 'depth', type: 'number', unit: 'mm', required: false },
        ],
    },
    {
        id: TunnelDamageType.Deformation,
        code: '05',
        category: DamageCategory.Volumetric,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'displacement', type: 'number', unit: 'mm', required: true },
            { field: 'direction', type: 'string', unit: null, required: false },
        ],
    },
    {
        id: TunnelDamageType.WaterLeakage,
        code: '06',
        category: DamageCategory.Environmental,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'level', type: 'number', unit: null, required: true, validation: { min: 1, max: 5 } },
            { field: 'flow_state', type: 'string', unit: null, required: false },
        ],
    },
    {
        id: TunnelDamageType.MaterialDeterioration,
        code: '07',
        category: DamageCategory.Surface,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [{ field: 'area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: TunnelDamageType.RebarExposure,
        code: '08',
        category: DamageCategory.Point,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'length', type: 'number', unit: 'mm', required: false },
            { field: 'corrosion_grade', type: 'string', unit: null, required: false },
        ],
    },
    {
        id: TunnelDamageType.CavityVoid,
        code: '09',
        category: DamageCategory.Volumetric,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'depth', type: 'number', unit: 'mm', required: false },
            { field: 'diameter', type: 'number', unit: 'mm', required: false },
        ],
    },
    {
        id: TunnelDamageType.Efflorescence,
        code: '10',
        category: DamageCategory.Environmental,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [{ field: 'area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: TunnelDamageType.FreeLime,
        code: '11',
        category: DamageCategory.Environmental,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [{ field: 'area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: TunnelDamageType.RustStaining,
        code: '12',
        category: DamageCategory.Environmental,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [{ field: 'area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: TunnelDamageType.Honeycombing,
        code: '13',
        category: DamageCategory.Surface,
        validGrades: ALL_SEVERITY_GRADES,
        measurementSchema: [
            { field: 'area', type: 'number', unit: 'm2', required: false },
            { field: 'depth', type: 'number', unit: 'mm', required: false },
        ],
    },
];
// ===== Bridge spec =====
const BRIDGE_COMPONENT_TYPES = [
    { id: BridgeComponentType.MainGirder, code: 'Mg' },
    { id: BridgeComponentType.CrossBeam, code: 'Cr' },
    { id: BridgeComponentType.Stringer, code: 'St' },
    { id: BridgeComponentType.DeckSlab, code: 'Ds' },
    { id: BridgeComponentType.SuperstructureOther, code: 'Uo' },
    { id: BridgeComponentType.Pier, code: 'Pr' },
    { id: BridgeComponentType.Abutment, code: 'Ab' },
    { id: BridgeComponentType.Foundation, code: 'Fd' },
    { id: BridgeComponentType.SubstructureOther, code: 'Lo' },
    { id: BridgeComponentType.Bearing, code: 'Bg' },
    { id: BridgeComponentType.ConnectionOther, code: 'Co' },
    { id: BridgeComponentType.ExpansionJoint, code: 'Ex' },
    { id: BridgeComponentType.FailSafe, code: 'Fs' },
    { id: BridgeComponentType.Curb, code: 'Cu' },
    { id: BridgeComponentType.Guardrail, code: 'Gr' },
    { id: BridgeComponentType.Drainage, code: 'Dr' },
    { id: BridgeComponentType.Pavement, code: 'Pv' },
    { id: BridgeComponentType.Staircase, code: 'Sc' },
    { id: BridgeComponentType.SignBoard, code: 'Sg' },
    { id: BridgeComponentType.Lighting, code: 'Lt' },
    { id: BridgeComponentType.InspectionGallery, code: 'Ig' },
];
const GRADES_FULL = [SeverityGrade.A, SeverityGrade.B, SeverityGrade.C, SeverityGrade.D, SeverityGrade.E];
const GRADES_AE = [SeverityGrade.A, SeverityGrade.E];
const BRIDGE_DAMAGE_TYPES = [
    {
        id: BridgeDamageType.CorrosionSteel,
        code: '01',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [
            {
                field: 'corrosion_area',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['local', 'spread', 'wide'] },
            },
            {
                field: 'thickness_reduction',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['none', 'superficial', 'measurable_loss'] },
            },
        ],
    },
    {
        id: BridgeDamageType.CrackSteel,
        code: '02',
        category: DamageCategory.Linear,
        validGrades: GRADES_AE,
        measurementSchema: [{ field: 'crack_length', type: 'number', unit: 'mm', required: true }],
    },
    {
        id: BridgeDamageType.BoltLooseningFalloff,
        code: '03',
        category: DamageCategory.Point,
        validGrades: GRADES_AE,
        measurementSchema: [{ field: 'bolt_count', type: 'number', unit: null, required: false }],
    },
    {
        id: BridgeDamageType.RuptureSteel,
        code: '04',
        category: DamageCategory.StructuralDamage,
        validGrades: GRADES_AE,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.AnticorrosionDeterioration,
        code: '05',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [{ field: 'deteriorated_area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: BridgeDamageType.CrackConcrete,
        code: '06',
        category: DamageCategory.Linear,
        validGrades: GRADES_FULL,
        measurementSchema: [
            { field: 'crack_width', type: 'number', unit: 'mm', required: true },
            {
                field: 'efflorescence_seepage',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['none', 'water_seepage', 'efflorescence', 'rust_stain'] },
            },
        ],
    },
    {
        id: BridgeDamageType.SpallingRebarExposure,
        code: '07',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [
            {
                field: 'exposure_area',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['limited', 'wide'] },
            },
            {
                field: 'section_loss',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['unmeasurable', 'measurable_loss'] },
            },
        ],
    },
    {
        id: BridgeDamageType.LeakageEfflorescence,
        code: '08',
        category: DamageCategory.Environmental,
        validGrades: GRADES_FULL,
        measurementSchema: [
            {
                field: 'leakage_state',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['none', 'water_leakage', 'free_lime', 'rust_stain'] },
            },
        ],
    },
    {
        id: BridgeDamageType.PunchingFailure,
        code: '09',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_AE,
        measurementSchema: [{ field: 'hole_area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: BridgeDamageType.ReinforcementDamage,
        code: '10',
        category: DamageCategory.Surface,
        validGrades: GRADES_AE,
        measurementSchema: [
            {
                field: 'material_type',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['steel_plate', 'fibers', 'cementitious', 'paint', 'steel_cover'] },
            },
        ],
    },
    {
        id: BridgeDamageType.CrackDeckSlab,
        code: '11',
        category: DamageCategory.Linear,
        validGrades: GRADES_FULL,
        measurementSchema: [
            {
                field: 'crack_pattern',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['one_directional', 'two_directional_lattice'] },
            },
        ],
    },
    {
        id: BridgeDamageType.Delamination,
        code: '12',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [{ field: 'delamination_area', type: 'number', unit: 'm2', required: false }],
    },
    {
        id: BridgeDamageType.JointAbnormality,
        code: '13',
        category: DamageCategory.Point,
        validGrades: GRADES_FULL,
        measurementSchema: [{ field: 'gap_width', type: 'number', unit: 'mm', required: false }],
    },
    {
        id: BridgeDamageType.RoadSurfaceUnevenness,
        code: '14',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_AE,
        measurementSchema: [{ field: 'level_difference', type: 'number', unit: 'mm', required: true }],
    },
    {
        id: BridgeDamageType.PavementAnomaly,
        code: '15',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [
            {
                field: 'anomaly_type',
                type: 'string',
                unit: null,
                required: false,
                validation: { oneOf: ['crack', 'rutting', 'pothole'] },
            },
        ],
    },
    {
        id: BridgeDamageType.BearingDysfunction,
        code: '16',
        category: DamageCategory.StructuralDamage,
        validGrades: GRADES_AE,
        measurementSchema: [
            {
                field: 'dysfunction_state',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['functioning', 'severely_obstructed', 'broken', 'uplifted'] },
            },
        ],
    },
    {
        id: BridgeDamageType.Other,
        code: '17',
        category: DamageCategory.Other,
        validGrades: GRADES_FULL,
        measurementSchema: [{ field: 'description', type: 'string', unit: null, required: false }],
    },
    {
        id: BridgeDamageType.AnchorageAbnormality,
        code: '18',
        category: DamageCategory.Point,
        validGrades: GRADES_AE,
        measurementSchema: [
            {
                field: 'damage_state',
                type: 'string',
                unit: null,
                required: true,
                validation: { oneOf: ['none', 'rust_stain', 'spalling', 'exposed_wedges'] },
            },
        ],
    },
    {
        id: BridgeDamageType.MaterialDeterioration,
        code: '19',
        category: DamageCategory.Surface,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.WaterLeakagePuddling,
        code: '20',
        category: DamageCategory.Environmental,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.AbnormalSoundVibration,
        code: '21',
        category: DamageCategory.Environmental,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.AbnormalDeflection,
        code: '22',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.DeformationDefect,
        code: '23',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.SedimentClogging,
        code: '24',
        category: DamageCategory.Environmental,
        validGrades: GRADES_FULL,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.SettlementTilt,
        code: '25',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_AE,
        measurementSchema: [],
    },
    {
        id: BridgeDamageType.Scour,
        code: '26',
        category: DamageCategory.Volumetric,
        validGrades: GRADES_AE,
        measurementSchema: [{ field: 'scour_depth', type: 'number', unit: 'm', required: false }],
    },
    {
        id: BridgeDamageType.None,
        code: '00',
        category: DamageCategory.Other,
        validGrades: [SeverityGrade.A],
        measurementSchema: [],
    },
];
export const InspectionSpec = {
    [FacilityType.Tunnel]: {
        componentTypes: TUNNEL_COMPONENT_TYPES,
        damageTypes: TUNNEL_DAMAGE_TYPES,
    },
    [FacilityType.Bridge]: {
        componentTypes: BRIDGE_COMPONENT_TYPES,
        damageTypes: BRIDGE_DAMAGE_TYPES,
    },
};
const isSupportedFacilityType = (facilityType) => facilityType === FacilityType.Tunnel || facilityType === FacilityType.Bridge;
/** Returns the inspection spec for a facility type, or null if unsupported. */
export const getInspectionSpec = (facilityType) => isSupportedFacilityType(facilityType) ? InspectionSpec[facilityType] : null;
export const getComponentTypeDefinitions = (facilityType) => getInspectionSpec(facilityType)?.componentTypes ?? [];
export const getDamageTypeDefinitions = (facilityType) => getInspectionSpec(facilityType)?.damageTypes ?? [];
export const getComponentTypeOptions = (facilityType) => getComponentTypeDefinitions(facilityType).map((c) => c.id);
export const getDamageTypeOptions = (facilityType) => getDamageTypeDefinitions(facilityType).map((d) => d.id);
export const getComponentTypeCode = (facilityType, id) => getComponentTypeDefinitions(facilityType).find((c) => c.id === id)?.code ?? '';
export const getDamageTypeDefinition = (facilityType, id) => getDamageTypeDefinitions(facilityType).find((d) => d.id === id);
export const getDamageTypeCode = (facilityType, id) => getDamageTypeDefinition(facilityType, id)?.code ?? '';
/** Valid severity grades for a damage type; falls back to all grades when unconstrained/unknown. */
export const getValidGrades = (facilityType, id) => getDamageTypeDefinition(facilityType, id)?.validGrades ?? ALL_SEVERITY_GRADES;
export const getMeasurementSchema = (facilityType, id) => getDamageTypeDefinition(facilityType, id)?.measurementSchema ?? [];
